import { useState, useRef } from "react";
import { Linkedin, Mail, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const emailSchema = z.string().trim().email("Please enter a valid email").max(255, "Email must be less than 255 characters");

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const honeypotRef = useRef<HTMLInputElement>(null);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Honeypot check - if filled, silently reject (bot detected)
    if (honeypotRef.current?.value) {
      setIsSubscribed(true);
      toast.success("Thank you for your interest!");
      return;
    }
    
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      toast.error(result.error.errors[0]?.message || "Please enter a valid email");
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from("newsletter_subscriptions")
        .insert({ email: result.data });

      if (error) {
        // Generic message to prevent email enumeration
        toast.info("Thank you for your interest!");
      } else {
        toast.success("Thank you for subscribing!");
      }
      setIsSubscribed(true);
      setEmail("");
    } catch {
      // Generic error to not reveal system details
      toast.info("Thank you for your interest!");
      setIsSubscribed(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer role="contentinfo">
      {/* Newsletter Section - Light Background */}
      <div className="bg-secondary">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <Mail className="h-10 w-10 mx-auto mb-4 text-gold" />
            <h3 className="font-display text-2xl md:text-3xl font-semibold mb-3 text-foreground">
              Stay Updated on Chicago Real Estate
            </h3>
            <p className="text-muted-foreground mb-6">
              Get exclusive listings, market insights, and neighborhood guides delivered to your inbox.
            </p>
            
            {isSubscribed ? (
              <div className="flex items-center justify-center gap-2 text-gold">
                <CheckCircle className="h-5 w-5" />
                <span className="text-foreground">You're subscribed! Check your inbox for updates.</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                {/* Honeypot field - hidden from users, catches bots */}
                <input
                  type="text"
                  name="website"
                  ref={honeypotRef}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                />
                <div className="flex-1">
                  <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                  <Input
                    id="newsletter-email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-gold"
                    disabled={isLoading}
                    aria-required="true"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="whitespace-nowrap"
                  aria-describedby="newsletter-description"
                >
                  {isLoading ? "Subscribing..." : "Subscribe"}
                </Button>
                <span id="newsletter-description" className="sr-only">Subscribe to newsletter for Chicago real estate updates</span>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Main Footer - Dark */}
      <div className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <Link to="/" className="flex flex-col mb-4">
                <span className="font-display text-2xl font-semibold">Brant Booker</span>
                <span className="text-[11px] tracking-[0.15em] text-primary-foreground/80 uppercase mt-1">
                  Jameson Sotheby's International Realty
                </span>
              </Link>
              <p className="text-primary-foreground/70 max-w-sm leading-relaxed mb-4">
                Dedicated to helping you find your perfect Chicago home. With integrity, 
                expertise, and personalized service.
              </p>
              <p className="text-primary-foreground/70 text-sm">
                A member of the Sotheby's International Realty network, providing access to 
                exceptional properties and a global network of discerning clients.
              </p>
              <div className="flex gap-4 mt-6">
                <a 
                  href="https://www.linkedin.com/in/brant-booker-098b4520"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-sm bg-primary-foreground/10 flex items-center justify-center hover:bg-gold/20 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                <a 
                  href="mailto:bbooker@jamesonsir.com"
                  className="w-10 h-10 rounded-sm bg-primary-foreground/10 flex items-center justify-center hover:bg-gold/20 transition-colors"
                  aria-label="Email"
                >
                  <Mail className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-3 text-primary-foreground/70">
                <li><a href="/#home" className="hover:text-gold transition-colors">Home</a></li>
                <li><a href="/#about" className="hover:text-gold transition-colors">About</a></li>
                <li><Link to="/properties" className="hover:text-gold transition-colors">Properties</Link></li>
                <li><Link to="/services" className="hover:text-gold transition-colors">Services</Link></li>
                <li><Link to="/contact" className="hover:text-gold transition-colors">Contact</Link></li>
              </ul>
            </div>

            {/* Neighborhoods */}
            <div>
              <h4 className="font-semibold mb-4">Areas Served</h4>
              <ul className="space-y-3 text-primary-foreground/70">
                <li><Link to="/neighborhoods/gold-coast" className="hover:text-gold transition-colors">Gold Coast</Link></li>
                <li><Link to="/neighborhoods/lincoln-park" className="hover:text-gold transition-colors">Lincoln Park</Link></li>
                <li><Link to="/neighborhoods/river-north" className="hover:text-gold transition-colors">River North</Link></li>
                <li><Link to="/neighborhoods/lakeview" className="hover:text-gold transition-colors">Lakeview</Link></li>
                <li><Link to="/neighborhoods/wicker-park" className="hover:text-gold transition-colors">Wicker Park</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-primary-foreground/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-primary-foreground/70 mb-4">
              <p>© {currentYear} Brant Booker | Jameson Sotheby's International Realty. All rights reserved.</p>
              <div className="flex gap-6">
                <Link to="/privacy" className="hover:text-primary-foreground transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-primary-foreground transition-colors">Terms of Service</Link>
                <Link to="/accessibility" className="hover:text-primary-foreground transition-colors">Accessibility</Link>
              </div>
            </div>
            <p className="text-xs text-primary-foreground/60 text-center">
              Sotheby's International Realty® is a registered trademark licensed to Sotheby's International Realty Affiliates LLC. 
              Each office is independently owned and operated.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
